/**
 * AppHost — SDK module loader.
 * Builtins for critical IME apps (Terminal). Everything else: dynamic ESM.
 * Passes windowId into ModuleContext so ctx.shell.menus / setTitle work.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createModuleContext } from "./api";
import { osStore } from "./store";
import { importModuleEsm } from "./load-module";
import { appMenuStore } from "./app-menu";
import { Loader2 } from "lucide-react";

type BuiltinMap = Record<string, () => ReactNode>;

export function AppHost({
  appId,
  version,
  windowId,
  builtins,
}: {
  appId: string;
  version?: string;
  windowId?: string;
  builtins: BuiltinMap;
}) {
  if (builtins[appId]) {
    return <>{builtins[appId]()}</>;
  }
  return (
    <DynamicModuleHost appId={appId} version={version} windowId={windowId} />
  );
}

function DynamicModuleHost({
  appId,
  version,
  windowId,
}: {
  appId: string;
  version?: string;
  windowId?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let unmount: (() => void) | undefined;
    // ctx.launchParams.subscribe registers with osStore, whose listener map
    // OUTLIVES this mount — an app that forgets its unsubscribe would keep
    // reacting to launches into a window that no longer exists. Same defensive
    // stance as WidgetHost takes with widget settings.
    const cleanups: Array<() => void> = [];
    let torndown = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (cancelled) return;
      const el = hostRef.current;
      if (!el) {
        setError("Mount node not ready");
        setLoading(false);
        return;
      }
      el.innerHTML = "";
      try {
        const ctx = createModuleContext({
          windowId,
          moduleVersion: version,
          appId,
          // Read at mount from the window record. Params are NOT an effect dep:
          // a re-launch must reach the running app through
          // ctx.launchParams.subscribe, not by remounting it and losing its state.
          launchParams: osStore.launchParamsFor(windowId),
          registerCleanup: (dispose) => {
            // The mount may have been torn down while `mount()` was awaiting —
            // park nothing, dispose immediately.
            if (torndown) dispose();
            else cleanups.push(dispose);
          },
        });
        const refPath = version ? `${appId}@${version}` : appId;
        const url = `/modules/${refPath}/index.js?t=${Date.now()}`;
        const mod = await importModuleEsm(url);
        const app =
          (mod as { default?: { mount?: Function; unmount?: Function } }).default ??
          mod;
        if (!app || typeof (app as { mount?: unknown }).mount !== "function") {
          throw new Error(
            "Module does not export default.mount(el, ctx). Keys: " +
              Object.keys(mod || {}).join(",")
          );
        }
        if (cancelled) return;
        await (
          app as {
            mount: (el: HTMLElement, ctx: unknown) => void | Promise<void>;
          }
        ).mount(el, ctx);
        if (typeof (app as { unmount?: unknown }).unmount === "function") {
          unmount = () => {
            try {
              (app as { unmount: (el?: HTMLElement) => void }).unmount(el);
            } catch {
              /* */
            }
          };
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setError(msg);
          console.error("[AppHost]", appId, e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
      torndown = true;
      unmount?.();
      // dispose is idempotent, so an app that already unsubscribed costs
      // nothing here. One throwing disposer must not strand the rest.
      for (const dispose of cleanups.splice(0)) {
        try {
          dispose();
        } catch {
          /* */
        }
      }
      if (windowId) appMenuStore.clear(windowId);
      if (hostRef.current) hostRef.current.innerHTML = "";
    };
  }, [appId, version, windowId]);

  return (
    <div className="app-host-root">
      {loading && !error && (
        <div className="app-host-status">
          <Loader2 size={18} className="spin" />
          <span>Loading {appId}…</span>
        </div>
      )}
      {error && (
        <div className="app-host-status error">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Couldn’t load app</div>
          <div style={{ opacity: 0.75, fontSize: 12 }}>{appId}</div>
          <pre
            style={{
              marginTop: 10,
              fontSize: 11,
              opacity: 0.7,
              textAlign: "left",
              maxWidth: 420,
            }}
          >
            {error}
          </pre>
        </div>
      )}
      <div
        ref={hostRef}
        className="app-host-mount"
        style={{
          height: "100%",
          visibility: error ? "hidden" : "visible",
          display: error ? "none" : "block",
        }}
      />
    </div>
  );
}
